import BigNumber from 'bignumber.js';
import { clsx } from 'clsx';
import { parseISO } from 'date-fns';
import React, { forwardRef, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import Text from '~/components/typographies/Text';
import { useGrossPricesState } from '~/services/items';
import { DocumentStatus, DocumentType, FinalizableDocumentType } from '~/types/document';
import {
  PreviewAdditionalData,
  RelatedDocumentLine,
  RelatedDocumentType,
  TransportInformations,
} from '~/types/document-builder';
import { ItalianEInvoice, StampType } from '~/types/invoicing';
import { isDefined } from '~/types/typeGuards';
import {
  eInvoiceFromInvoiceToForm,
  fromEInvoiceDettaglioLineeToItemsPreview,
  fromEinvoiceToPreview,
} from '~/utils/einvoice';
import { formatDateForDisplay, formatFromISOToDisplay } from '~/utils/formatDate';
import { formatPercentageTwoDigits } from '~/utils/formatNumber';
import {
  calcWithholdingAmount,
  withholdingMotivationOptions,
  withholdingTypeOptions,
} from '~/utils/invoicing/withholding';
import { formatMoney, isNotZero, negateMoney, newMoney, subtractMoney, sumMoney } from '~/utils/money';
import {
  TextLabel,
  TextAmount,
  TextTaxableDetailLabel,
  TextTaxableDetailAmount,
} from '~/views/invoices/builder/SummaryComponents';
import { ItemsTable } from '~/views/invoices/preview/ItemsTable';
import styles from './SibillPreviewContent.module.scss';

export interface PreviewProps {
  additionalData: PreviewAdditionalData;
  eInvoice: ItalianEInvoice;
  showDraft?: boolean;
  provisionalNumber?: string;
  disableTabularNums?: boolean;
}

const CompanyLogo: React.FC<{ additionalData: PreviewAdditionalData }> = ({ additionalData }) => {
  if (additionalData.type === DocumentType.SelfInvoice) return null;
  if (!additionalData.companyLogo.large) return null;

  return (
    <div>
      <img className={styles.logo} src={additionalData.companyLogo.large} alt="company_logo" />
    </div>
  );
};

interface RelatedKeyValueProps {
  label: string;
  value: string;
}

const RelatedKeyValue = ({ label, value }: RelatedKeyValueProps) => (
  <div className={styles.keyValue}>
    <Text variant="body2" weight="semibold">
      {label}
    </Text>
    <Text variant="body2" weight="regular">
      {value}
    </Text>
  </div>
);

interface RelatedDocumentsSectionProps {
  documents: RelatedDocumentLine[];
  title: string;
}
const RelatedDocumentsSection = ({ title, documents }: RelatedDocumentsSectionProps) => {
  const { t } = useTranslation('documents', { keyPrefix: 'builder' });
  if (documents.length === 0) {
    return null;
  }

  return (
    <div className={styles.section}>
      <Text variant="subtitle1" weight={undefined}>
        {title}
      </Text>
      {documents.map((relatedInvoice, index) => (
        <div className={styles.row} key={index}>
          <RelatedKeyValue label={t('preview.invoice_id')} value={relatedInvoice.documentNumber} />
          {relatedInvoice.date && (
            <RelatedKeyValue label={t('preview.date')} value={formatDateForDisplay(relatedInvoice.date)} />
          )}

          {relatedInvoice.CIGCode && (
            <RelatedKeyValue label={t('preview.related_CIG')} value={relatedInvoice.CIGCode} />
          )}
          {relatedInvoice.CUPCode && (
            <RelatedKeyValue label={t('preview.related_CUP')} value={relatedInvoice.CUPCode} />
          )}
          {relatedInvoice.orderCode && (
            <RelatedKeyValue label={t('preview.related_order')} value={relatedInvoice.orderCode} />
          )}
        </div>
      ))}
    </div>
  );
};

const FormatDestination = ({ destination }: { destination: TransportInformations['destination'] }) => {
  const { t } = useTranslation('countries');
  const country = destination.country ? ` (${t(destination.country)})` : '';

  return [destination.address, destination.city, destination.postalCode, country].filter(Boolean).join(' ');
};

const TransportInformationSection = (transportInformationData: TransportInformations) => {
  const { t } = useTranslation('documents', { keyPrefix: 'builder' });
  return (
    <div className={styles.transportInformationContainer}>
      <div className={styles.transportInformationLine}>
        <div>
          <Text variant={'body2'} weight={'semibold'}>
            {t('preview.transport_information_destination')}
          </Text>
          <Text variant={'body2'} weight={'regular'}>
            <FormatDestination destination={transportInformationData.destination} />
          </Text>
        </div>
      </div>
      <div className={styles.transportInformationLine}>
        <div>
          <Text variant={'body2'} weight={'semibold'}>
            {t('preview.transport_information_transport_reason')}
          </Text>
          <Text variant={'body2'} weight={'regular'}>
            {transportInformationData.transportReason}
          </Text>
        </div>
      </div>
      <div className={styles.transportInformationLine}>
        <div>
          <Text variant={'body2'} weight={'semibold'}>
            {t('preview.transport_information_weight')}
          </Text>
          <Text variant={'body2'} weight={'regular'}>
            {`${transportInformationData.weight}kg`}
          </Text>
        </div>
        <div>
          <Text variant={'body2'} weight={'semibold'}>
            {t('preview.transport_information_courier')}
          </Text>
          <Text variant={'body2'} weight={'regular'}>
            {transportInformationData.courier}
          </Text>
        </div>
        <div>
          <Text variant={'body2'} weight={'semibold'}>
            {t('preview.transport_information_packs_number')}
          </Text>
          <Text variant={'body2'} weight={'regular'}>
            {transportInformationData.packsNumber}
          </Text>
        </div>
      </div>
    </div>
  );
};

const DeliveryContentSignaturesSection = () => {
  const { t } = useTranslation('documents', { keyPrefix: 'builder' });
  return (
    <div className={styles.deliveryNoteSignatures}>
      <Text variant={'body2'} weight={'semibold'}>
        {t('preview.delivery_note_signature_sender')}
      </Text>
      <Text variant={'body2'} weight={'semibold'}>
        {t('preview.delivery_note_signature_courier')}
      </Text>
      <Text variant={'body2'} weight={'semibold'}>
        {t('preview.delivery_note_signature_recipient')}
      </Text>
    </div>
  );
};

const SibillPreviewContent = forwardRef<HTMLDivElement, PreviewProps>((props, ref) => {
  const { additionalData, disableTabularNums, eInvoice, showDraft, provisionalNumber } = props;

  const { t } = useTranslation('documents', { keyPrefix: 'builder' });
  const { t: genericT } = useTranslation();
  const [useGrossPrices] = useGrossPricesState();

  const values = eInvoiceFromInvoiceToForm(eInvoice, additionalData.type, useGrossPrices);

  const totals = fromEinvoiceToPreview(eInvoice, additionalData.type);

  const isDeliveryNote = values.type === DocumentType.DeliveryNote;
  const isQuote = values.type === DocumentType.Quote;

  const { currency } = values;

  const withholdingTaxInfo = values.withholdingTax[0];
  const withholdingPercentage = withholdingTaxInfo ? BigNumber(withholdingTaxInfo.percentage) : null;
  const withholdingType = withholdingTypeOptions.find((o) => o.value === withholdingTaxInfo?.type)?.label.toLowerCase();

  const withholdingMotivation = withholdingMotivationOptions
    .find((o) => o.value === withholdingTaxInfo?.motivation)
    ?.label.toLowerCase();

  const showWithholdingMessage =
    isDefined(withholdingType) && isDefined(withholdingMotivation) && isDefined(withholdingPercentage);

  const isSelfInvoice = values.type === DocumentType.SelfInvoice;

  const header = eInvoice.fatturaElettronicaHeader;
  const sender = header.cedentePrestatore;
  const recipient = header.cessionarioCommittente;

  const showUnit = values.items.some((item) => !!item.unit);
  const showPrice = (!isDeliveryNote || values.includePrices) ?? false;
  const showDiscount = values.items.some((item) => item.discount.value !== '0');
  const showCode = values.items.some((item) => !!item.code);

  const body = eInvoice.fatturaElettronicaBody[0]!;
  const institutionName = body.datiPagamento[0]?.dettaglioPagamento[0]?.istitutoFinanziario;
  const number = provisionalNumber ?? body.datiGenerali.datiGeneraliDocumento.numero;

  const isDraft = showDraft && additionalData.status === DocumentStatus.Draft;

  const orderDocuments = values.relatedDocuments.filter((d) => d.type === RelatedDocumentType.OrdineAcquisto);
  const contractDocuments = values.relatedDocuments.filter((d) => d.type === RelatedDocumentType.Contratto);
  const agreementDocuments = values.relatedDocuments.filter((d) => d.type === RelatedDocumentType.Convenzione);
  const receptionDocuments = values.relatedDocuments.filter((d) => d.type === RelatedDocumentType.Ricezione);
  const relatedInvoices = values.relatedDocuments.filter((d) => d.type === RelatedDocumentType.FatturaCollegata);
  const relatedDdt = values.relatedDocuments.filter((d) => d.type === RelatedDocumentType.Ddt);

  const eInvoiceType = body.datiGenerali.datiGeneraliDocumento.tipoDocumento;

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.previewBody}>
        <div className={styles.topRow}>
          <Text variant="title3" weight="semibold">
            {isDraft && <>{t('preview.draft')} </>}
            {!additionalData.isElectronic
              ? t('preview.document_type', {
                  context: values.type as FinalizableDocumentType,
                  number: isDraft ? '' : number,
                })
              : t('preview.document_type_electronic', {
                  context: values.type as FinalizableDocumentType,
                  number: isDraft ? '' : number,
                })}
          </Text>
          <Text variant="body1" weight="semibold">
            {t('preview.issuing_date', { date: formatDateForDisplay(values.date) })}
          </Text>
        </div>

        <div className={styles.fromTo}>
          <div className={styles.companyInfo}>
            <Text variant="body1" weight="semibold">
              {isSelfInvoice ? t('preview.issued_by_self_invoice') : t('preview.issued_by')}
            </Text>
            <div className={styles.fromContainer}>
              <CompanyLogo additionalData={additionalData} />
              <div>
                <Text element="p" variant="body2" weight="semibold">
                  {sender.datiAnagrafici.anagrafica.denominazione}
                </Text>
                <Text element="p" variant="body2" weight="regular">
                  {t('preview.VAT_number')}
                  {sender.datiAnagrafici.idFiscaleIva.idCodice}
                </Text>
                <Text element="p" variant="body2" weight="regular">
                  {t('preview.tax_number')}
                  {sender.datiAnagrafici.codiceFiscale}
                </Text>
                <Text element="p" variant="body2" weight="regular">
                  {sender.sede.comune}, {sender.sede.provincia} {sender.sede.indirizzo} {sender.sede.nazione}
                </Text>
              </div>
            </div>
          </div>

          <div className={styles.companyInfo}>
            <Text variant="body1" weight="semibold">
              {isSelfInvoice ? t('preview.to_self_invoice') : t('preview.to')}
            </Text>
            <div>
              <Text element="p" variant="body2" weight="semibold">
                {recipient.datiAnagrafici.anagrafica.denominazione}
              </Text>
              {recipient.datiAnagrafici.idFiscaleIva?.idCodice && (
                <Text element="p" variant="body2" weight="regular">
                  {t('preview.VAT_number')}
                  {recipient.datiAnagrafici.idFiscaleIva.idCodice}
                </Text>
              )}
              <Text element="p" variant="body2" weight="regular">
                {t('preview.tax_number')}
                {recipient.datiAnagrafici.codiceFiscale}
              </Text>
              <Text element="p" variant="body2" weight="regular">
                {recipient.sede.comune}, {recipient.sede.provincia} {recipient.sede.indirizzo} {recipient.sede.nazione}
              </Text>
              {header.datiTrasmissione.codiceDestinatario && (
                <Text element="p" variant="body2" weight="regular">
                  {t('preview.destination_code')}
                  {header.datiTrasmissione.codiceDestinatario}
                </Text>
              )}
              {header.datiTrasmissione.pecDestinatario && (
                <Text element="p" variant="body2" weight="regular">
                  {t('preview.certified_email')}
                  {header.datiTrasmissione.pecDestinatario}
                </Text>
              )}
              {sender.riferimentoAmministrazione && (
                <Text element="p" variant="body2" weight="regular">
                  {t('preview.riferimento_amministrazione')}
                  {sender.riferimentoAmministrazione}
                </Text>
              )}
            </div>
          </div>
        </div>
        <div className={styles.paymentInfo}>
          {!isDeliveryNote && (
            <div className={styles.keyValue}>
              <Text variant="body2" weight="semibold">
                {t('preview.payment_method')}
              </Text>
              <Text variant="body2" weight="regular">
                {genericT(values.paymentInfo.paymentMethod)}
              </Text>
            </div>
          )}
          {institutionName && !isDeliveryNote && (
            <div className={styles.keyValue}>
              <Text variant="body2" weight="semibold">
                {t('preview.institution')}
              </Text>
              <Text variant="body2" weight="regular">
                {institutionName}
              </Text>
            </div>
          )}
          {values.paymentInfo.paymentAccount && !isDeliveryNote && (
            <div className={styles.keyValue}>
              <Text variant="body2" weight="semibold">
                {t('preview.payment_details')}
              </Text>
              <Text variant="body2" weight="regular">
                {values.paymentInfo.paymentAccount}
              </Text>
            </div>
          )}
          {isDeliveryNote && values.transportInformations && (
            <TransportInformationSection {...values.transportInformations} />
          )}
        </div>
        <div className={styles.paymentInfo}>
          {!isDeliveryNote &&
            body.datiPagamento
              .flatMap((d) => d.dettaglioPagamento)
              .sort((a, b) => {
                const da = a.dataScadenzaPagamento;
                const db = b.dataScadenzaPagamento;

                if (!da && !db) return 0;
                if (!da) return 1;
                if (!db) return -1;

                return parseISO(da).getTime() - parseISO(db).getTime();
              })
              .map((flow, index) => (
                <div key={index} className={styles.keyValue}>
                  <Text variant="body2" weight="semibold">
                    {t('preview.deadline_number', { number: index + 1 })}
                  </Text>
                  <Text variant="body2" weight="regular">
                    {flow.dataScadenzaPagamento && formatFromISOToDisplay(flow.dataScadenzaPagamento)}{' '}
                    {formatMoney({ amount: flow.importoPagamento, currency })}
                  </Text>
                </div>
              ))}
        </div>
        {!isDeliveryNote && !isQuote && (
          <div className={styles.paymentInfo}>
            <div className={styles.keyValue}>
              <Text variant="body2" weight="semibold">
                {t('preview.invoice_type')}
              </Text>
              <Text variant="body2" weight="regular">
                {t(`preview.invoice_types.${eInvoiceType}`)} {/* qui */}
              </Text>
            </div>
          </div>
        )}
        {!isDeliveryNote && showWithholdingMessage && (
          <div className={styles.paymentInfo}>
            <div className={styles.keyValue}>
              <Text variant="body2" weight="semibold">
                {t('preview.withholding_tax')}
              </Text>
              <div className={styles.value}>
                <Text variant="body2" weight="regular">
                  {t('preview.withholding_tax_text', {
                    withholdingType,
                    withholdingPercentage: formatPercentageTwoDigits(withholdingPercentage.toNumber()),
                    withholdingMotivation,
                  })}
                </Text>
              </div>
            </div>
          </div>
        )}
        {!isDeliveryNote && values.motivation && (
          <div className={styles.section}>
            <Text variant="subtitle1" weight={undefined}>
              {t('preview.motivation')}
            </Text>
            <Text element="p" variant="body2" weight="regular">
              {values.motivation}
            </Text>
          </div>
        )}

        <div className={styles.itemsTable}>
          <ItemsTable
            type={values.type}
            currency={currency}
            items={fromEInvoiceDettaglioLineeToItemsPreview(
              body.datiBeniServizi.dettaglioLinee,
              currency,
              body.datiBeniServizi.datiRiepilogo,
            )}
            showCode={showCode}
            showUnit={showUnit}
            showPrice={showPrice}
            showDiscount={showDiscount}
          />
        </div>

        {showPrice && (
          <div className={styles.totalsContainer}>
            <TextLabel>{t('preview.total_before_vat')}</TextLabel>
            <TextAmount tabularNums={!disableTabularNums} className={styles.amount}>
              {formatMoney(
                sumMoney(totals.netTotal, totals.socialSecurity?.importoContributoCassa ?? newMoney(0, currency)),
              )}
            </TextAmount>
            {(totals.socialSecurity || values.stamp?.type === StampType.Customer) && (
              <>
                <TextTaxableDetailLabel className={styles.taxableDetail}>
                  {t('sections.products_and_services')}
                </TextTaxableDetailLabel>
                <TextTaxableDetailAmount className={clsx(styles.amount, styles.taxableDetail)}>
                  {formatMoney(subtractMoney(totals.netTotal, newMoney(values.stamp?.value ?? 0, currency)))}
                </TextTaxableDetailAmount>
              </>
            )}
            {totals.socialSecurity && (
              <>
                <TextTaxableDetailLabel className={styles.taxableDetail}>
                  {t('labels.social_security', {
                    socialSecurityPercentage: formatPercentageTwoDigits(
                      BigNumber(totals.socialSecurity.alCassa).toNumber(),
                    ),
                    netTotal: formatMoney(totals.socialSecurity.imponibileCassa),
                  })}
                </TextTaxableDetailLabel>
                <TextTaxableDetailAmount className={clsx(styles.amount, styles.taxableDetail)}>
                  {formatMoney(totals.socialSecurity.importoContributoCassa)}
                </TextTaxableDetailAmount>
              </>
            )}
            {values.stamp?.type === StampType.Customer && (
              <>
                <TextTaxableDetailLabel className={styles.taxableDetail}>{t('labels.stamp')}</TextTaxableDetailLabel>
                <TextTaxableDetailAmount className={clsx(styles.amount, styles.taxableDetail)}>
                  {formatMoney(newMoney(values.stamp.value, currency))}
                </TextTaxableDetailAmount>
              </>
            )}
            <hr className={styles.divider} />

            {values.type !== DocumentType.DeliveryNote &&
              totals.vatTotals.map((total) => (
                <Fragment key={`${total.vatRate}_${total.vatExemption}_${total.lawReference}`}>
                  <TextLabel>
                    {t('preview.vat_percentage_total', {
                      percentage: formatPercentageTwoDigits(parseFloat(total.vatRate)),
                      netTotal: formatMoney(total.taxable),
                    })}
                    {total.vatExemption && <> ({total.vatExemption})</>}
                  </TextLabel>
                  <TextAmount tabularNums={!disableTabularNums} className={styles.amount}>
                    {formatMoney(total.vatAmount)}
                  </TextAmount>
                  {total.lawReference && <TextLabel className={styles.lawReference}>{total.lawReference}</TextLabel>}
                  <hr className={styles.divider} />
                </Fragment>
              ))}

            {(isNotZero(totals.alreadyPaidTotal) || totals.documentTotal.amount !== totals.totalToPay.amount) && (
              <>
                <Text variant="subtitle2" className={styles.total}>
                  {t('labels.document_total')}
                </Text>
                <Text
                  variant="subtitle2"
                  className={clsx(styles.total, styles.amount)}
                  tabularNums={!disableTabularNums}
                >
                  {formatMoney(totals.documentTotal)}
                </Text>
                <hr className={styles.divider} />
              </>
            )}

            {values.withholdingTax.map((item, index) => (
              <React.Fragment key={`${item.type}-${item.percentage}-${index}`}>
                <TextLabel>
                  {t('labels.withholding_tax', {
                    withholdingPercentage: formatPercentageTwoDigits(BigNumber(item.percentage).toNumber()),
                    netTotal: formatMoney({ amount: item.taxableAmount, currency }),
                    context: item.type,
                  })}
                </TextLabel>
                <TextAmount tabularNums={!disableTabularNums} className={styles.amount}>
                  {formatMoney(negateMoney(newMoney(calcWithholdingAmount(item), totals.netTotal.currency)))}
                </TextAmount>
                <hr className={styles.divider} />
              </React.Fragment>
            ))}
            {totals.vatTotals
              .filter((total) => !total.shouldBePaid)
              .map((total) => (
                <Fragment key={total.vatRate}>
                  <TextLabel>{t('labels.vat')}</TextLabel>
                  <TextAmount tabularNums={!disableTabularNums} className={styles.amount}>
                    {formatMoney(negateMoney(total.vatAmount))}
                  </TextAmount>
                  <hr className={styles.divider} />
                </Fragment>
              ))}

            {isNotZero(totals.alreadyPaidTotal) && (
              <>
                <TextLabel>{t('labels.already_paid')}</TextLabel>
                <TextAmount tabularNums={!disableTabularNums} className={styles.amount}>
                  {formatMoney(negateMoney(totals.alreadyPaidTotal))}
                </TextAmount>
                <hr className={styles.divider} />
              </>
            )}

            <Text variant="subtitle2" className={styles.total}>
              {values.type === DocumentType.CreditNote ? t('preview.total_amount') : t('preview.total_to_pay')}
            </Text>
            <Text variant="subtitle2" tabularNums={!disableTabularNums} className={clsx(styles.total, styles.amount)}>
              {formatMoney(totals.totalToPay)}
            </Text>
            {values.stamp?.type === StampType.Issuer && (
              <>
                <hr className={styles.divider} />
                <TextLabel>{t('labels.stamp')}</TextLabel>
                <TextAmount className={styles.amount}>{formatMoney(newMoney(values.stamp.value, currency))}</TextAmount>
              </>
            )}
          </div>
        )}
        {relatedDdt.length > 0 && (
          <div className={styles.section}>
            <Text variant="subtitle1" weight={undefined}>
              {t('preview.transport_data')}
            </Text>
            <div className={styles.ddtContainer}>
              {relatedDdt.map((transportItem) => (
                <>
                  <div className={styles.keyValue}>
                    <Text variant="body2" weight="semibold">
                      {t('preview.transport_document_number')}
                    </Text>
                    <Text variant="body2" weight="regular">
                      {transportItem.documentNumber}
                    </Text>
                  </div>
                  <div className={styles.keyValue}>
                    <Text variant="body2" weight="semibold">
                      {t('preview.date')}
                    </Text>
                    {transportItem.date && (
                      <Text variant="body2" weight="regular">
                        {formatDateForDisplay(transportItem.date)}
                      </Text>
                    )}
                  </div>
                </>
              ))}
            </div>
          </div>
        )}

        <RelatedDocumentsSection documents={relatedInvoices} title={t('preview.related_invoices')} />
        <RelatedDocumentsSection documents={orderDocuments} title={t('preview.related_orders')} />
        <RelatedDocumentsSection documents={contractDocuments} title={t('preview.related_contracts')} />
        <RelatedDocumentsSection documents={agreementDocuments} title={t('preview.related_agreements')} />
        <RelatedDocumentsSection documents={receptionDocuments} title={t('preview.related_receptions')} />

        {additionalData.notes && (
          <div className={styles.section}>
            <Text variant="subtitle1" weight={undefined}>
              {t('preview.notes')}
            </Text>
            <Text element="p" variant="body2" weight="regular" className={styles.notes}>
              {additionalData.notes}
            </Text>
          </div>
        )}
      </div>
      {isDeliveryNote && (
        <>
          <div className={styles.centralSpacer} />
          <div>
            <DeliveryContentSignaturesSection />
          </div>
        </>
      )}
    </div>
  );
});

export default SibillPreviewContent;
